import { z } from "zod";

/*
 * The project dashboard (docs/dashboard.md): where each object sits on the
 * canvas, shared by all members, and the compact metrics of server cards.
 */

/** Object kinds that have a stored position; location frames follow their members. */
export const DashboardNodeKind = z.enum([
  "internet",
  "server",
  "public_ip",
  "network",
  "firewall",
  "volume",
  "floating_ip",
  "load_balancer",
]);
export type DashboardNodeKind = z.infer<typeof DashboardNodeKind>;

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/** `internet`, or `<kind>:<our id>` such as `server:0190…`. */
export const DashboardNodeKey = z
  .string()
  .regex(
    new RegExp(
      `^(internet|(server|public_ip|network|firewall|volume|floating_ip|load_balancer):${UUID})$`,
    ),
    "Not a node key",
  );
export type DashboardNodeKey = z.infer<typeof DashboardNodeKey>;

/** Canvas coordinates in pixels at zoom 1; far beyond any real layout, but bounded. */
export const DASHBOARD_COORDINATE_LIMIT = 1_000_000;
const Coordinate = z
  .number()
  .finite()
  .min(-DASHBOARD_COORDINATE_LIMIT)
  .max(DASHBOARD_COORDINATE_LIMIT);

export const DashboardPosition = z.object({ node: DashboardNodeKey, x: Coordinate, y: Coordinate });
export type DashboardPosition = z.infer<typeof DashboardPosition>;

/** Most positions one request may carry; a project with more objects arranges in parts. */
export const DASHBOARD_MAX_POSITIONS = 2000;

export const DashboardLayout = z.object({ positions: z.array(DashboardPosition) });
export type DashboardLayout = z.infer<typeof DashboardLayout>;

export const PutDashboardLayoutRequest = z
  .object({
    positions: z.array(DashboardPosition).max(DASHBOARD_MAX_POSITIONS),
    /** Drop every stored position that is not in `positions` ("Arrange"). */
    replace: z.boolean().default(false),
  })
  .refine((v) => new Set(v.positions.map((p) => p.node)).size === v.positions.length, {
    message: "Each node at most once",
    path: ["positions"],
  });
export type PutDashboardLayoutRequest = z.input<typeof PutDashboardLayoutRequest>;

/** Most servers one metrics request may ask for: the cards that fit on a screen. */
export const DASHBOARD_METRICS_MAX_SERVERS = 12;

export const DashboardMetricsQuery = z.object({
  /** Our server ids, comma-separated. */
  serverIds: z
    .string()
    .transform((v) => [...new Set(v.split(",").filter(Boolean))])
    .pipe(z.array(z.uuid()).min(1).max(DASHBOARD_METRICS_MAX_SERVERS)),
});

/** `[unix seconds, value]`; null where the provider has no sample. */
const Points = z.array(z.tuple([z.number(), z.number().nullable()]));

/** The short history a server card draws; empty series for servers that are not running. */
export const DashboardServerMetrics = z.object({
  serverId: z.uuid(),
  /** CPU percent as the provider reports it (the same series as the server's metrics tab). */
  cpu: Points,
  networkIn: Points,
  networkOut: Points,
  diskReadIops: Points,
  diskWriteIops: Points,
  /** Set when the provider could not answer for this server; the other servers still come. */
  error: z.string().nullable(),
});
export type DashboardServerMetrics = z.infer<typeof DashboardServerMetrics>;

export const DashboardMetrics = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  stepSeconds: z.number().int().positive(),
  servers: z.array(DashboardServerMetrics),
});
export type DashboardMetrics = z.infer<typeof DashboardMetrics>;
